// pages/admin/gifts/form.jsx
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { giftsService } from "@/api/awards";
import TaleButton from "@components/TaleButton";
import { TaleInput } from "@components/TaleInput";
import { TaleTextarea } from "@components/TaleTextarea";
import { TaleSelect } from "@components/TaleSelect";
import { TaleSwitch } from "@components/TaleSwitch";
import { FiCheckCircle, FiGift, FiTrash2, FiUpload } from "react-icons/fi";
import { TaleDashbaordMainCard } from "@components/TaleDashbaordMainCard";
import { useTranslation } from "@/hooks/useTranslation";

const GiftForm = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    costPoints: "",
    type: "SHOPPING_VOUCHER",
    display: true,
  });

  const giftTypes = [
    { value: "SHOPPING_VOUCHER", label: t("giftManagement.gifts.form.typeCoupon") },
    { value: "PHONE_RECHARGE_CARD", label: t("giftManagement.gifts.form.typePhoneRecharge") },
  ];

  useEffect(() => {
    if (isEditMode) {
      loadGift();
    }
  }, [id]);

  const loadGift = async () => {
    setLoading(true);
    try {
      const gift = await giftsService.getGiftById(id);
      setFormData({
        name: gift.name || "",
        description: gift.description || "",
        costPoints: gift.costPoints || "",
        type: gift.type || "COUPON",
        display: gift.display !== undefined ? gift.display : true,
      });

      if (gift.fileUrl) {
        setPreview(gift.fileUrl);
      }
    } catch (error) {
      console.error("Failed to load gift:", error);
      alert(t("giftManagement.gifts.form.loadFailed"));
      navigate("/admin/gifts");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Check file size (max 5MB)
    if (selectedFile.size > 5 * 1024 * 1024) {
      alert(t("giftManagement.gifts.form.fileTooLarge"));
      return;
    }

    // Check file type
    if (!selectedFile.type.startsWith("image/")) {
      alert(t("giftManagement.gifts.form.selectImageFile"));
      return;
    }

    setFile(selectedFile);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleRemoveImage = () => {
    setFile(null);
    setPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.costPoints) {
      alert(t("giftManagement.gifts.form.fillRequired"));
      return;
    }

    setUploading(true);
    try {
      const giftDto = {
        ...formData,
        costPoints: parseInt(formData.costPoints),
        cardAmount :1000
      };

      if (isEditMode) {
        await giftsService.updateGift(id, giftDto, file);
        alert(t("giftManagement.gifts.form.updateSuccess"));
      } else {
        await giftsService.createGift(giftDto, file);
        alert(t("giftManagement.gifts.form.createSuccess"));
      }

      navigate("/admin/gifts");
    } catch (error) {
      console.error("Failed to save gift:", error);
      alert(t("giftManagement.gifts.form.saveFailed", { message: error.message }));
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <TaleDashbaordMainCard>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">{isEditMode ? t("giftManagement.gifts.form.editTitle") : t("giftManagement.gifts.form.createTitle")}</h1>
        <p className="text-gray-600">{isEditMode ? t("giftManagement.gifts.form.editSubtitle") : t("giftManagement.gifts.form.createSubtitle")}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white p-6 rounded-2xl border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">{t("giftManagement.gifts.form.basicInformation")}</h2>

              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <TaleInput label={t("giftManagement.gifts.form.giftNameLabel")} name="name" value={formData.name} onChange={handleInputChange} placeholder={t("giftManagement.gifts.form.giftNamePlaceholder")} required />

                  <TaleSelect
                    label={t("giftManagement.gifts.form.giftTypeLabel")}
                    name="type"
                    value={formData.type}
                    onChange={(value) => {
                      setFormData((prev) => ({ ...prev, type: value.target.value }));
                    }}
                    required
                  >
                    {giftTypes.map((gift) => (
                      <option key={gift.value} value={gift.value}>
                        {gift.label}
                      </option>
                    ))}
                  </TaleSelect>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <TaleInput label={t("giftManagement.gifts.form.costPointsLabel")} name="costPoints" type="number" value={formData.costPoints} onChange={handleInputChange} placeholder={t("giftManagement.gifts.form.costPointsPlaceholder")} min="1" required />

                  <div className="flex items-center justify-center md:justify-end pt-6">
                    <TaleSwitch label={t("giftManagement.gifts.form.displayToUsersLabel")} name="display" checked={formData.display} onChange={(checked) => setFormData((prev) => ({ ...prev, display: checked }))} description={t("giftManagement.gifts.form.displayToUsersDescription")} />
                  </div>
                </div>
                <div>
                  <TaleTextarea label={t("giftManagement.gifts.form.descriptionLabel")} name="description" value={formData.description} onChange={handleInputChange} placeholder={t("giftManagement.gifts.form.descriptionPlaceholder")} rows={6} description={t("giftManagement.gifts.form.descriptionHint")} />
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Image Upload */}
          <div className="space-y-8">
            {/* Image Upload Card */}
            <div className="bg-white p-6 rounded-2xl border border-gray-200 sticky top-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">{t("giftManagement.gifts.form.giftImage")}</h2>

              <div className="space-y-6">
                {/* Upload Area */}
                <div className={`border-2 border-dashed rounded-2xl transition-all duration-200 ${preview ? "border-transparent" : "border-gray-300 hover:border-blue-400 hover:bg-blue-50/50"}`}>
                  {preview ? (
                    <div className="space-y-4">
                      <div className="relative rounded-xl overflow-hidden bg-gray-50">
                        <img src={preview} alt="Preview" className="w-full h-auto max-h-96 object-contain" />
                        <button type="button" onClick={handleRemoveImage} className="absolute top-4 right-4 rtl:right-auto rtl:left-4 bg-white/90 backdrop-blur-sm text-gray-700 p-2 rounded-full hover:bg-white hover:text-red-600 transition-all shadow-lg" title={t("giftManagement.gifts.form.removeImageTitle")}>
                          <FiTrash2 className="w-5 h-5" />
                        </button>
                      </div>

                      <div className="flex items-center justify-between px-2">
                        <span className="text-sm text-gray-600">{t("giftManagement.gifts.form.previewLabel")}</span>
                        <button type="button" onClick={handleRemoveImage} className="text-sm text-red-600 hover:text-red-700 font-medium">
                          {t("giftManagement.gifts.form.removeLabel")}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <label className="block cursor-pointer p-8">
                      <div className="flex flex-col items-center justify-center text-center">
                        <div className="w-20 h-20 mb-4 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                          <FiUpload className="w-10 h-10 text-gray-400" />
                        </div>
                        <p className="text-gray-700 font-medium mb-2">{t("giftManagement.gifts.form.uploadImage")}</p>
                        <p className="text-gray-500 text-sm mb-1">{t("giftManagement.gifts.form.dragDropHint")}</p>
                        <p className="text-gray-400 text-xs">{t("giftManagement.gifts.form.fileTypeHint")}</p>
                      </div>
                      <input type="file" className="hidden" accept="image/*" onChange={handleFileChange} />
                    </label>
                  )}
                </div>

                {/* Upload Button */}
                <div>
                  <label className="block">
                    <input type="file" className="hidden" accept="image/*" onChange={handleFileChange} />
                    <div className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer text-center font-medium transition-colors">{preview ? t("giftManagement.gifts.form.changeImage") : t("giftManagement.gifts.form.browseFiles")}</div>
                  </label>
                </div>

                {/* File Info */}
                {file && (
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-900 truncate">{file.name}</span>
                      <span className="text-sm text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-green-600">
                      <FiCheckCircle className="w-4 h-4" />
                      <span>{t("giftManagement.gifts.form.readyToUpload")}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Actions */}
        <div className=" bottom-0 bg-white border-t border-gray-200 py-6 px-8 mt-8">
          <div className="flex items-center justify-between mx-auto">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <FiGift className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">{isEditMode ? t("giftManagement.gifts.form.updateGiftLabel") : t("giftManagement.gifts.form.createTitle")}</p>
                <p className="text-sm text-gray-500">{isEditMode ? t("giftManagement.gifts.form.reviewChangesHint") : t("giftManagement.gifts.form.fillRequiredHint")}</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <TaleButton type="button" variant="outlined" onClick={() => navigate("/admin/gifts")} disabled={uploading} className="min-w-[120px]">
                {t("common.cancel")}
              </TaleButton>

              <TaleButton type="submit" variant="gradient" disabled={uploading} className="min-w-[180px] flex items-center justify-center gap-2">
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    {t("giftManagement.gifts.form.processing")}
                  </>
                ) : (
                  <>{isEditMode ? t("giftManagement.gifts.form.saveChanges") : t("giftManagement.gifts.form.createGiftButton")}</>
                )}
              </TaleButton>
            </div>
          </div>
        </div>
      </form>
    </TaleDashbaordMainCard>
  );
};

export default GiftForm;
